import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FtoReportTaskStatusService } from '../task-status-service';
import { GetTaskDetail, type ApiGetTaskDetailResponse } from '@/infrastructure/api-functions/core-drafting-api/get-task-detail-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { CommandService, Emitter, LocationService, Logger, type Event } from '@patsnap/tahiti-core';
import type { FtoReportTaskStatus } from '../../types';
import { FtoTaskStep } from '../../enums';
import { FtoReportTaskStatusModel } from '../../models/task-status-model';
import { TaskDateFormatter } from '@/services/date-format/task-date-formatter';
import { FtoReportRetrievalResultReportModel } from '../../models/retrieval-result-report-model';
import { CurrentTaskInputModel } from '@/domains/task/models/current-task-input-model';
import { I18nService } from '@telecom/shared/services/i18n';
import { AbCheckApi } from '@telecom/shared/domain/ab';

declare module '@patsnap/tahiti-core' {
    interface LocationService {
        href: string;
    }
}

@injectable()
export class FtoReportTaskStatusServiceImpl implements FtoReportTaskStatusService {

    // eslint-disable-next-line max-params
    constructor(
        @inject(GetTaskDetail)
        private readonly _getTaskDetail: GetTaskDetail<string>,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(FtoReportTaskStatusModel)
        private readonly _noveltyCheckTaskStatusModel: FtoReportTaskStatusModel,
        @inject(TaskDateFormatter)
        private readonly _taskDateFormatter: TaskDateFormatter,
        @inject(LocationService)
        private readonly _locationService: LocationService,
        @inject(FtoReportRetrievalResultReportModel)
        private readonly _retrievalResultReportModel: FtoReportRetrievalResultReportModel,
        @inject(CurrentTaskInputModel)
        private readonly _currentTaskInputModel: CurrentTaskInputModel,
        @inject(I18nService)
        private readonly _i18nService: I18nService,
        @inject(AbCheckApi)
        private readonly _abCheckApi: AbCheckApi,
        @inject(CommandService)
        private readonly _commandService: CommandService,
    ) { }

    private _serverSideTaskStep: FtoTaskStep = FtoTaskStep.INITIAL;

    get serverSideTaskStep(): FtoTaskStep {
        return this._serverSideTaskStep;
    }

    private _onServerSideTaskStepEmitter = new Emitter<FtoTaskStep>();

    get onServerSideTaskStep(): Event<FtoTaskStep> {
        return this._onServerSideTaskStepEmitter.event;
    }

    private async hasFtoProPermission() {
        return this._abCheckApi.checkAbTest('fto-pro');
    }


    async pullTaskStatus(): Promise<FtoReportTaskStatus> {
        const taskId = this._currentTaskService.taskId;
        this._logger.info('获取任务状态', taskId);
        const data = await this._getTaskStatusRepository(taskId);
        this._noveltyCheckTaskStatusModel.update(() => data);
        this._currentTaskInputModel.update((state) => {
            state.inputLanguage = data.inputLanguage;
            return state;
        });
        this._retrievalResultReportModel.update((state) => {
            state.userInput = data.userInput;
            state.techEssential = state.techEssential || data.userInput;
            return state;
        });
        this._logger.info('获取任务状态成功', taskId, data);
        return Promise.resolve(data);
    }

    private async _getTaskStatusRepository(taskId: string): Promise<FtoReportTaskStatus> {
        const response = await this._getTaskDetail.getTaskDetail(taskId);
        const taskStep = this._getStepFromUrl() || this._convertTaskStep(response.current_step);
        this._serverSideTaskStep = taskStep;
        this._onServerSideTaskStepEmitter.fire(taskStep);
        return {
            taskStatus: response.task_status,
            taskStep,
            lastUpdateTime: this._taskDateFormatter.format(response.updated_at),
            userInput: response.user_input,
            inputLanguage: this._getInputLanguage(response),
            isHistoryData: response.is_history_data,
        };
    }

    private _getInputLanguage(response: ApiGetTaskDetailResponse<string>): string {
        if (response.input_lang) {
            return response.input_lang.toUpperCase();
        }
        return this._i18nService.locale.value.toUpperCase();
    }

    /**
     * 从 url 获取 ?currentStep={step} 中获取 step 的值
     */
    private _getStepFromUrl(): FtoTaskStep | undefined {
        const url = this._locationService.href;
        const searchParams = new URLSearchParams(url.split('?')[1]);
        const step = searchParams.get('currentStep');
        try {
            const taskStep = this._convertTaskStep(step!);
            this._logger.info('从 url 获取 step 成功', taskStep);
            return taskStep;
        }
        catch (error) {
            this._logger.warn('从 url 获取 step 失败', error);
            return undefined;
        }
    }

    // eslint-disable-next-line complexity
    private _convertTaskStep(step: string): FtoTaskStep {
        if (step === '') {
            return FtoTaskStep.INITIAL;
        }
        else if (step === 'FTO_SEARCH_INPUT') {
            return FtoTaskStep.FTO_SEARCH_INPUT;
        }
        else if (step === 'FTO_SEARCH_TECH_FEATURE_START') {
            return FtoTaskStep.TECH_FEATURE_PULL_START;
        }
        else if (step === 'FTO_SEARCH_TECH_FEATURE') {
            return FtoTaskStep.TECH_FEATURE_PULL_SUCCESS;
        }
        else if (step === 'CONFIRM') {
            return FtoTaskStep.SEARCH_END;
        }
        else if (step === 'RESULT') {
            return FtoTaskStep.REPORT_CREATED;
        }
        throw new Error(`未知任务步骤: ${step}`);
    }
}
